import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { EchoflicksSessionEvent } from "../types";
import { continuityAuditFromEvents } from "./continuityAudit";

const shortDigest = (value?: string) =>
  value ? `${value.slice(0, 12)}…` : "—";
const label = (value?: string) => value?.replaceAll("_", " ") ?? "—";

export const ContinuityAuditCard = ({
  events,
}: {
  events: EchoflicksSessionEvent[];
}) => {
  const audit = continuityAuditFromEvents(events);
  if (!audit) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Cinematic continuity audit</CardTitle>
          <Badge variant={audit.evaluated?.enabled ? "default" : "outline"}>
            {audit.evaluated?.enabled ? "Enabled for this Echo" : "Not enabled"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Product flow</dt>
            <dd>{label(audit.evaluated?.productFlow)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Evaluation</dt>
            <dd>{label(audit.evaluated?.reason)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Config release</dt>
            <dd className="font-mono text-xs">
              {audit.evaluated?.configReleaseId ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Config digest</dt>
            <dd
              className="font-mono text-xs"
              title={audit.evaluated?.configDigest}
            >
              {shortDigest(audit.evaluated?.configDigest)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Plan</dt>
            <dd className="font-mono text-xs">{audit.plan?.planId ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Policy</dt>
            <dd>{audit.plan?.policyVersion ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Composer validation</dt>
            <dd>
              {audit.validation
                ? audit.validation.valid
                  ? "Accepted"
                  : label(audit.validation.reason)
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Degradations</dt>
            <dd>{audit.degradations.length}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">I2V attempts</dt>
            <dd>{audit.attempts.length}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">QC observations</dt>
            <dd>{audit.qc.length}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Continuity cost</dt>
            <dd>
              {audit.cost?.estimatedCostUsd
                ? `$${audit.cost.estimatedCostUsd}`
                : "—"}
            </dd>
          </div>
        </dl>

        {audit.configReadback ? (
          <div>
            <div className="mb-2 font-medium">
              Runtime configuration provenance
            </div>
            <dl className="grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Desired", audit.configReadback.desiredReleaseId],
                ["Published", audit.configReadback.publishedReleaseId],
                ["Applied", audit.configReadback.appliedReleaseId],
                ["Frozen on Echo", audit.configReadback.frozenReleaseId],
              ].map(([name, value]) => (
                <div key={name}>
                  <dt className="text-muted-foreground">{name}</dt>
                  <dd className="font-mono">{value ?? "—"}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {audit.fusion ? (
          <div>
            <div className="mb-2 flex items-center gap-2 font-medium">
              Fusion bridge
              <Badge
                variant={
                  audit.fusion.qcDisposition === "accepted"
                    ? "default"
                    : "outline"
                }
              >
                {label(
                  audit.fusion.qcDisposition ?? audit.fusion.executionStatus,
                )}
              </Badge>
            </div>
            <dl className="grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Bridge</dt>
                <dd className="font-mono">{audit.fusion.bridgeId}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Strategy / join</dt>
                <dd>
                  {label(audit.fusion.strategy)} /{" "}
                  {label(audit.fusion.joinMethod)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Still candidates</dt>
                <dd>
                  {audit.fusion.acceptedCandidateCount ?? 0} accepted of{" "}
                  {audit.fusion.candidateCount ?? 0}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Erasure</dt>
                <dd>
                  {label(audit.fusion.erasureStatus)} (
                  {audit.fusion.deletedObjectCount ?? 0} objects)
                </dd>
              </div>
            </dl>
            {audit.fusion.qcIssueCodes.length ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Enforced QC: {audit.fusion.qcIssueCodes.map(label).join(", ")}
              </p>
            ) : null}
            {audit.fusion.personSafety ? (
              <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                {(["outgoing", "incoming"] as const).map((direction) => {
                  const probe = audit.fusion?.personSafety?.[direction];
                  if (!probe) return null;
                  return (
                    <div key={direction} className="rounded border p-2">
                      <div className="font-medium capitalize">
                        {direction} person safety
                      </div>
                      <div className="text-muted-foreground">
                        {label(probe.state)} / {label(probe.reasonCode)} /{" "}
                        {probe.sampledFrameCount ?? 0} frames
                      </div>
                      <div className="text-muted-foreground">
                        {probe.policyVersion ?? "—"} / confidence{" "}
                        {probe.primaryConfidence?.toFixed(2) ?? "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        {audit.finalContent ? (
          <div className="rounded-md border p-3">
            <div className="font-medium">Final timeline proof</div>
            <p className="mt-1 text-xs text-muted-foreground">
              {audit.finalContent.selectedSceneIds.length} selected scenes /{" "}
              {audit.finalContent.fallbackSceneIds.length} fallbacks /{" "}
              {audit.finalContent.probedDurationSeconds?.toFixed(2) ?? "—"}s
            </p>
            <div className="mt-3 space-y-2 text-xs">
              {audit.finalContent.selections.map((selection, index) => (
                <div
                  key={`${selection.sceneId ?? index}-${selection.assetLocatorSha256 ?? index}`}
                  className="rounded border p-2"
                >
                  Scene {selection.sceneId ?? "—"}:{" "}
                  {label(selection.continuityDisposition ?? "ordinary")} /{" "}
                  {label(selection.providerId ?? selection.assetProvenance)}
                  <div className="font-mono text-muted-foreground">
                    {shortDigest(selection.assetLocatorSha256)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {audit.plan?.seams.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-left text-xs">
              <thead className="border-b text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Boundary</th>
                  <th className="py-2 pr-3 font-medium">Plan</th>
                  <th className="py-2 pr-3 font-medium">Execution</th>
                  <th className="py-2 pr-3 font-medium">Identity</th>
                  <th className="py-2 pr-3 font-medium">Confidence</th>
                  <th className="py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.plan.seams.map((seam) => (
                  <tr key={seam.seamId} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono">{seam.boundary}</td>
                    <td className="py-2 pr-3">{label(seam.strategy)}</td>
                    <td className="py-2 pr-3">{label(seam.execution)}</td>
                    <td className="py-2 pr-3">{label(seam.identityRisk)}</td>
                    <td className="py-2 pr-3">
                      {seam.confidence?.toFixed(2) ?? "—"}
                    </td>
                    <td className="py-2">
                      {seam.reasons.map(label).join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {audit.degradations.length ? (
          <div>
            <div className="mb-2 font-medium">Safe fallbacks</div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {audit.degradations.map((item, index) => (
                <li
                  key={`${item.seamId ?? item.sceneId ?? "unknown"}-${index}`}
                >
                  {item.seamId ?? `scene ${item.sceneId ?? "unknown"}`}:{" "}
                  {label(item.reason)} → {label(item.fallback)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {audit.attempts.length ? (
          <div className="overflow-x-auto">
            <div className="mb-2 font-medium">Continuity I2V attempts</div>
            <table className="w-full min-w-[720px] text-left text-xs">
              <thead className="border-b text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Seam</th>
                  <th className="py-2 pr-3 font-medium">Attempt</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Provider / model</th>
                  <th className="py-2 pr-3 font-medium">End frame</th>
                  <th className="py-2 pr-3 font-medium">Latency</th>
                  <th className="py-2 font-medium">Fallback / error</th>
                </tr>
              </thead>
              <tbody>
                {audit.attempts.map((attempt) => (
                  <tr
                    key={attempt.attemptId}
                    className="border-b last:border-0"
                  >
                    <td className="py-2 pr-3 font-mono">{attempt.seamId}</td>
                    <td className="py-2 pr-3">{attempt.attempt ?? "—"}</td>
                    <td className="py-2 pr-3">{label(attempt.status)}</td>
                    <td className="py-2 pr-3">
                      {label(attempt.concreteProvider ?? attempt.provider)}
                      {attempt.model ? ` / ${attempt.model}` : ""}
                    </td>
                    <td className="py-2 pr-3">
                      {attempt.endFrame
                        ? `${attempt.endFrame.requested ? "requested" : "absent"} / ${attempt.endFrame.applied ? "applied" : "not applied"} / ${attempt.endFrame.hashAgreement === true ? "hash match" : "hash unverified"}`
                        : "—"}
                    </td>
                    <td className="py-2 pr-3">
                      {attempt.elapsedMs === undefined
                        ? "—"
                        : `${attempt.elapsedMs} ms`}
                    </td>
                    <td className="py-2">
                      {attempt.fallback ? "fallback" : "primary"}
                      {attempt.errorCode ? ` / ${attempt.errorCode}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {audit.qc.length ? (
          <div className="overflow-x-auto">
            <div className="mb-2 font-medium">Continuity QC</div>
            <table className="w-full min-w-[620px] text-left text-xs">
              <thead className="border-b text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Seam</th>
                  <th className="py-2 pr-3 font-medium">Attempt</th>
                  <th className="py-2 pr-3 font-medium">Disposition</th>
                  <th className="py-2 pr-3 font-medium">Issues</th>
                  <th className="py-2 font-medium">Recommended fallback</th>
                </tr>
              </thead>
              <tbody>
                {audit.qc.map((item) => (
                  <tr
                    key={`${item.seamId}:${item.attempt ?? "unknown"}`}
                    className="border-b last:border-0"
                  >
                    <td className="py-2 pr-3 font-mono">{item.seamId}</td>
                    <td className="py-2 pr-3">{item.attempt ?? "—"}</td>
                    <td className="py-2 pr-3">{label(item.disposition)}</td>
                    <td className="py-2 pr-3">
                      {item.issueCodes.map(label).join(", ") || "—"}
                    </td>
                    <td className="py-2">{label(item.recommendedFallback)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
